/**
 * Channel detail page — (tf3-35a; B-F14) one of the owner's connected channels, from the package's store: its name,
 * picture, platform and status; Reconnect (when it needs it) and Disconnect, the same calls and words as Settings; and the
 * videos posted to it from TubeForge with each one's numbers (the numbers panel's read, GET /api/videos/:id/post-numbers).
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { channelsApi, type Channel } from "@/api/channels.ts";
import { videosApi, publishingApi } from "@/api/videos.ts";
import { connectInPopup, connectErrorText, takeKeptConnectResult, CONNECT_MESSAGE, type ConnectResult } from "@/lib/connect-popup.ts";
import { disconnectWords } from "@/lib/channel-status.ts";
import { platformOf, statusLine, videoLabel, noPostedHere, readScope, unreadableLine, POSTED_VIDEOS_READ } from "@/lib/package-channels.ts";
import { NUMBER_COLUMNS, cell, statusOf, NUMBERS_NOT_AVAILABLE, NOT_REPORTED_NOTE, needsDashNote, type PostNumbersRow } from "@/lib/post-numbers.ts";
import { PageContainer, TfCard, Btn, TfSpinner } from "@/components/v2/primitives.tsx";

export function ChannelDetailPage() {
  const channelId = useParams<{ channelId: string }>().channelId ?? "";   // the router decodes it (channelPath encodes)
  const list = useQuery({ queryKey: ["publishing", "channels"], queryFn: () => channelsApi.list() });
  const channel = list.data?.channels.find((c) => c.channelId === channelId) ?? null;
  // (session audit 10-17-B, B1) what a Disconnect did is kept here: the channel then leaves the list (revoked), and the
  //  words — Instagram's own step, a revoke the platform didn't confirm — must still be read
  const [said, setSaid] = useState<string | null>(null);
  if (said) return <PageContainer title="Channel"><TfCard><p role="status" style={{ margin: 0 }}>{said} <Link to="/channels">All channels</Link></p></TfCard></PageContainer>;

  if (list.isLoading) return <PageContainer><div style={{ display: "flex", justifyContent: "center", padding: 60 }}><TfSpinner size={32} /></div></PageContainer>;
  if (list.error) return <PageContainer title="Channel"><TfCard><p style={{ margin: 0, color: "var(--tf-danger, #dc2626)" }}>Your channels couldn't be read: {(list.error as Error).message}</p></TfCard></PageContainer>;
  if (!channel) return <PageContainer title="Channel"><TfCard><p style={{ margin: 0 }}>This channel isn't connected. <Link to="/channels">All channels</Link></p></TfCard></PageContainer>;
  return <ChannelView channel={channel} onDisconnected={setSaid} />;
}

function ChannelView({ channel, onDisconnected }: { channel: Channel; onDisconnected: (words: string) => void }) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const line = statusLine(channel);
  const platform = platformOf(channel.channel);
  // (audit 10-17-B, B8) both lists of the package's channels: this page's and the post form's
  const refresh = () => { void queryClient.invalidateQueries({ queryKey: ["publishing", "channels"] }); void queryClient.invalidateQueries({ queryKey: ["publishing-channels"] }); };
  // (audit 10-17-B, B7) a reconnect's result, as Settings reads it: the popup's message, or one kept in this window
  useEffect(() => {
    const show = (r: ConnectResult) => { setErr(r.error ? connectErrorText(r.error) : null); refresh(); };
    const onMsg = (e: MessageEvent) => { if (e.origin === window.location.origin && e.data?.type === CONNECT_MESSAGE) show(e.data.result as ConnectResult); };
    window.addEventListener("message", onMsg);
    const kept = takeKeptConnectResult(channel.channel);
    if (kept) show(kept);
    return () => window.removeEventListener("message", onMsg);
  }, [channel.channel]);   // eslint-disable-line react-hooks/exhaustive-deps
  // called straight from the click (a popup opened outside the click is blocked), as Settings does
  const reconnect = () => {
    setErr(null); setBusy(true);
    connectInPopup(channel.channel, publishingApi.connect)
      .catch((e: Error) => setErr(e.message || "The connection could not be started."))   // catch-reason: shown to the person
      .finally(() => { setBusy(false); refresh(); });
  };
  const disconnect = async () => {
    if (!window.confirm(`Disconnect ${channel.accountName} (${platform})? TubeForge stops posting to it and deletes what it kept from it.`)) return;
    setErr(null); setBusy(true);
    try { onDisconnected(disconnectWords(platform, await channelsApi.disconnect(channel))); refresh(); }
    catch (e) { setErr((e as Error).message || "The channel couldn't be disconnected."); }   // catch-reason: shown to the person
    finally { setBusy(false); }
  };
  return (
    <PageContainer title={channel.accountName} subtitle={platform}>
      <TfCard style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
          {channel.avatarUrl ? <img src={channel.avatarUrl} alt="" width={56} height={56} style={{ borderRadius: 28, objectFit: "cover" }} />
            : <div style={{ width: 56, height: 56, borderRadius: 28, background: "var(--tf-surface)" }} />}
          <div style={{ flex: 1, minWidth: 200 }}>
            <div role="status" style={{ fontSize: 13, color: line.reconnect ? "var(--tf-warning, #d97706)" : "var(--tf-success, #16a34a)" }}>{line.text}</div>
          </div>
          {line.reconnect && <Btn onClick={reconnect} disabled={busy}>Reconnect</Btn>}
          <Btn variant="ghost" onClick={disconnect} disabled={busy}>Disconnect</Btn>
        </div>
        {err && <p style={{ fontSize: 13, margin: "12px 0 0", color: "var(--tf-danger, #dc2626)" }}>{err}</p>}
      </TfCard>
      <PostedHere channelId={channel.channelId} />
    </PageContainer>
  );
}

/** The videos posted to this channel (of the most recent published ones), with each one's numbers here. */
function PostedHere({ channelId }: { channelId: string }) {
  const q = useQuery({
    queryKey: ["channel-posts", channelId],
    queryFn: async () => {
      const { videos } = await videosApi.list({ limit: 50 });
      const posted = videos.filter((v) => v.status === "published").slice(0, POSTED_VIDEOS_READ);
      // (audit 10-17-B, B2) each video's read on its own: one that fails is counted, never the whole list
      const settled = await Promise.allSettled(posted.map(async (v) => ({ v, a: await videosApi.postNumbers(v.id) })));
      const read = settled.flatMap((x) => (x.status === "fulfilled" ? [x.value] : []));
      return { available: read.every((x) => x.a.available), looked: posted.length, unreadable: settled.length - read.length,
        rows: read.flatMap(({ v, a }) => a.rows.filter((r) => r.channelId === channelId).map((r) => ({ video: v, r }))) };
    },
  });
  if (q.isLoading) return <TfSpinner />;
  if (q.error) return <p style={{ fontSize: 13, color: "var(--tf-danger, #dc2626)" }}>The videos posted here couldn't be read: {(q.error as Error).message}</p>;
  const data = q.data!;
  if (!data.available) return <TfCard><p className="tf-muted" style={{ margin: 0 }}>{NUMBERS_NOT_AVAILABLE}</p></TfCard>;
  const unread = data.unreadable ? <p style={{ fontSize: 12, margin: "8px 0 0", color: "var(--tf-danger, #dc2626)" }}>{unreadableLine(data.unreadable)}</p> : null;
  if (data.rows.length === 0) return <TfCard><p className="tf-muted" style={{ margin: 0 }}>{noPostedHere(data.looked)}</p>{unread}</TfCard>;
  const rows: PostNumbersRow[] = data.rows.map((x) => x.r);
  return (
    <TfCard>
      <h2 style={{ fontSize: 16, margin: "0 0 10px" }}>Posted here</h2>
      <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>
        <thead><tr><th style={{ textAlign: "left" }}>Video</th>{NUMBER_COLUMNS.map(([k, l]) => <th key={k} style={{ textAlign: "right" }}>{l}</th>)}</tr></thead>
        <tbody>{data.rows.map(({ video, r }) => (
          <tr key={r.targetId}>
            <td><Link to={`/videos/${video.id}`}>{videoLabel({ ...video, createdAt: String(video.createdAt) })}</Link></td>
            {statusOf(r) ? <td colSpan={NUMBER_COLUMNS.length} className="tf-muted">{statusOf(r)}</td>
              : NUMBER_COLUMNS.map(([k]) => <td key={k} style={{ textAlign: "right" }}>{cell(r, k)}</td>)}
          </tr>))}
        </tbody>
      </table>
      {needsDashNote(rows) && <p className="tf-muted" style={{ fontSize: 12, margin: "8px 0 0" }}>{NOT_REPORTED_NOTE}</p>}
      <p className="tf-muted" style={{ fontSize: 12, margin: "8px 0 0" }}>{readScope(data.looked)}</p>
      {unread}
    </TfCard>
  );
}

